import { useEffect, useMemo, useRef, useState } from 'react';
import { readPackageDayOffsets, packageDayPayloadFromForm } from '../utils/packageDayFields.js';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { trekAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import AdminBackLink from '../components/AdminBackLink.jsx';
import './ModuleAdmin.css';
import FullIconSelect from '../components/FullIconSelect.jsx';
import { priceInputString } from '../utils/formatAdminPrice.js';

const normalizeIncludesItems = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw
    .filter((item) => item && String(item.title || '').trim())
    .map((item, idx) => ({
      icon: String(item.icon || '').trim() || 'briefcase',
      title: String(item.title || '').trim(),
      value: String(item.value ?? '').trim(),
      order: idx
    }));
};

/** One-time read: old includes_config rows → editable includesItems (admin can fix values). */
const legacyIncludesConfigToItems = (cfg) => {
  if (!Array.isArray(cfg) || cfg.length === 0) return [];
  return cfg
    .filter((item) => item && item.enabled !== false && String(item.label || item.key || '').trim())
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
    .map((item, idx) => ({
      icon: String(item.icon || '').trim() || 'briefcase',
      title: String(item.label || item.key || '').trim(),
      value: '',
      order: idx
    }));
};

const emptyPackage = {
  name: '',
  description: '',
  price: '',
  vacancy: '',
  startDayOffset: 0,
  endDayOffset: 0,
  priority: 0,
  imageUrl: '',
  reportingMessage: '',
  ticketRequired: false,
  ticketDeparture: false,
  ticketDepartureAddon: false,
  ticketReturn: false,
  ticketReturnAddon: false,
  hidden: false,
  enabledScheduleIds: [],
  includesItems: []
};

const TrekPackageForm = () => {
  const { trekId, packageId } = useParams();
  const isEdit = packageId && packageId !== 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { data: trekRes } = useQuery(
    ['trek', trekId],
    () => trekAPI.get(trekId),
    { enabled: !!trekId }
  );
  const trek = trekRes?.data?.data;

  const { data: scheduleRes } = useQuery(
    ['trek-schedule', trekId],
    () => trekAPI.listScheduleDays(trekId),
    { enabled: !!trekId }
  );
  const scheduleDays = scheduleRes?.data?.data || [];

  const { data: packageListRes } = useQuery(
    ['trek-packages', trekId],
    () => trekAPI.listPackages(trekId),
    { enabled: isEdit && !!trekId }
  );
  const existingPackage = useMemo(() => {
    if (!isEdit || !packageId) return null;
    const fromList = (packageListRes?.data?.data || []).find((p) => String(p.id) === String(packageId));
    if (fromList) return fromList;
    return (trek?.packages || []).find((p) => String(p.id) === String(packageId)) || null;
  }, [isEdit, packageId, packageListRes?.data?.data, trek?.packages]);

  const [form, setForm] = useState(emptyPackage);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [draggingScheduleId, setDraggingScheduleId] = useState(null);
  const [dragOverScheduleId, setDragOverScheduleId] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);


  useEffect(() => {
    if (existingPackage) {
      const { start, end } = readPackageDayOffsets(existingPackage);
      setForm({
        ...emptyPackage,
        name: existingPackage.name || '',
        description: existingPackage.description || '',
        price: existingPackage.price != null ? priceInputString(existingPackage.price) : '',
        vacancy: existingPackage.vacancy != null
          ? String(existingPackage.vacancy)
          : (existingPackage.defaultVacancy != null ? String(existingPackage.defaultVacancy) : ''),
        startDayOffset: start,
        endDayOffset: end,
        priority: existingPackage.priority != null ? existingPackage.priority : 0,
        imageUrl: existingPackage.imageUrl || existingPackage.image || '',
        reportingMessage: existingPackage.reportingMessage || '',
        ticketRequired: Boolean(existingPackage.ticketRequired),
        ticketDeparture: Boolean(existingPackage.ticketDeparture),
        ticketDepartureAddon: Boolean(existingPackage.ticketDepartureAddon),
        ticketReturn: Boolean(existingPackage.ticketReturn),
        ticketReturnAddon: Boolean(existingPackage.ticketReturnAddon),
        hidden: Boolean(existingPackage.hidden),
        enabledScheduleIds: Array.isArray(existingPackage.enabledScheduleIds)
          ? existingPackage.enabledScheduleIds
          : [],
        includesItems: normalizeIncludesItems(
          Array.isArray(existingPackage.includesItems) && existingPackage.includesItems.length > 0
            ? existingPackage.includesItems
            : legacyIncludesConfigToItems(existingPackage.includesConfig)
        )
      });
    }
  }, [existingPackage]);

  const mutation = useMutation(
    (payload) =>
      isEdit
        ? trekAPI.updatePackage(trekId, packageId, payload)
        : trekAPI.createPackage(trekId, payload),
    {
      onSuccess: (response) => {
        if (!isMountedRef.current) return;
        queryClient.invalidateQueries(['trek-packages', trekId]);
        if (!isEdit) {
          const createdId = response?.data?.data?.id;
          if (createdId) navigate(`/treks/${trekId}/packages/${createdId}`, { replace: true });
        }
        const repriced = Number(response?.data?.priceSync?.updated || 0);
        showToast({
          message:
            isEdit && repriced > 0
              ? response?.data?.message || `Package updated. Repriced ${repriced} not-fully-paid booking(s).`
              : isEdit
                ? 'Package updated successfully.'
                : 'Package created successfully.',
          severity: 'success'
        });
      }
    }
  );

  const buildPayload = () => ({
    name: form.name.trim(),
    description: form.description.trim(),
    price: form.price ? Number(form.price) : null,
    // Keep compatibility aliases plus actual model keys
    vacancy: form.vacancy ? Number(form.vacancy) : null,
    defaultVacancy: form.vacancy ? Number(form.vacancy) : null,
    ...packageDayPayloadFromForm(form),
    priority: Number(form.priority || 0),
    imageUrl: form.imageUrl || null,
    image: form.imageUrl || null,
    reportingMessage: form.reportingMessage || '',
    ticketRequired: Boolean(form.ticketRequired),
    ticketDeparture: Boolean(form.ticketDeparture),
    ticketDepartureAddon: Boolean(form.ticketDepartureAddon),
    ticketReturn: Boolean(form.ticketReturn),
    ticketReturnAddon: Boolean(form.ticketReturnAddon),
    hidden: Boolean(form.hidden),
    enabledScheduleIds: form.enabledScheduleIds || [],
    travelBusAdjust: 0,
    travelTrainAdjust: 0,
    travelFlightAdjust: 0,
    stayQuadAdjust: 0,
    stayTripleAdjust: 0,
    stayDoubleAdjust: 0,
    includesItems: normalizeIncludesItems(form.includesItems),
    // Clear legacy JSON when saving from this form; empty list → event page uses trek fees-includes / defaults.
    includesConfig: null
  });

  const handleSubmit = () => {
    if (!form.name.trim()) {
      showToast({
        message: 'Package Name is required.',
        severity: 'warning'
      });
      return;
    }
    const payload = buildPayload();
    mutation.mutate(payload);
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsUploadingImage(true);
      const response = await trekAPI.uploadImage(file);
      const url = response?.data?.data?.url;
      if (!url) throw new Error('Upload failed');
      setForm((prev) => ({ ...prev, imageUrl: url }));
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || 'Failed to upload image.',
        severity: 'error'
      });
    } finally {
      setIsUploadingImage(false);
      e.target.value = '';
    }
  };

  const toggleSchedule = (dayId, enable) => {
    setForm((prev) => {
      const current = Array.isArray(prev.enabledScheduleIds) ? [...prev.enabledScheduleIds] : [];
      const hasDay = current.some((id) => String(id) === String(dayId));
      if (enable && !hasDay) return { ...prev, enabledScheduleIds: [...current, dayId] };
      if (!enable && hasDay) {
        return {
          ...prev,
          enabledScheduleIds: current.filter((id) => String(id) !== String(dayId))
        };
      }
      return prev;
    });
  };

  const reorderEnabledSchedules = (dragId, targetId) => {
    if (!dragId || !targetId || String(dragId) === String(targetId)) return;
    setForm((prev) => {
      const list = Array.isArray(prev.enabledScheduleIds) ? [...prev.enabledScheduleIds] : [];
      const fromIdx = list.findIndex((id) => String(id) === String(dragId));
      const toIdx = list.findIndex((id) => String(id) === String(targetId));
      if (fromIdx < 0 || toIdx < 0) return prev;
      const [moved] = list.splice(fromIdx, 1);
      list.splice(toIdx, 0, moved);
      return { ...prev, enabledScheduleIds: list };
    });
  };

  const enabledDays = (form.enabledScheduleIds || [])
    .map((id) => scheduleDays.find((d) => String(d.id) === String(id)))
    .filter(Boolean);
  const availableDays = scheduleDays.filter(
    (d) => !(form.enabledScheduleIds || []).some((id) => String(id) === String(d.id))
  );

  const setIncludeItemRow = (idx, patch) => {
    setForm((prev) => {
      const next = Array.isArray(prev.includesItems) ? [...prev.includesItems] : [];
      next[idx] = { ...(next[idx] || {}), ...patch };
      return { ...prev, includesItems: next };
    });
  };

  const addIncludeItemRow = () => {
    setForm((prev) => ({
      ...prev,
      includesItems: [
        ...(Array.isArray(prev.includesItems) ? prev.includesItems : []),
        { icon: 'briefcase', title: '', value: '', order: Array.isArray(prev.includesItems) ? prev.includesItems.length : 0 }
      ]
    }));
  };

  const removeIncludeItemRow = (idx) => {
    setForm((prev) => ({
      ...prev,
      includesItems: (Array.isArray(prev.includesItems) ? prev.includesItems : []).filter((_, i) => i !== idx)
    }));
  };

  const moveIncludeItemRow = (idx, direction) => {
    setForm((prev) => {
      const list = Array.isArray(prev.includesItems) ? [...prev.includesItems] : [];
      const target = idx + direction;
      if (target < 0 || target >= list.length) return prev;
      [list[idx], list[target]] = [list[target], list[idx]];
      return { ...prev, includesItems: list.map((item, i) => ({ ...(item || {}), order: i })) };
    });
  };

  return (
    <div className="module-page">
      <AdminBackLink to="/treks" state={{ openTrekId: trekId }} label="Back to treks" />
      <div className="module-header">
        <div>
          <h1 className="module-title">{isEdit ? 'Edit Package' : 'Add Package'}</h1>
          <p className="module-subtitle">
            {trek ? trek.name : 'Configure trek package details'}
          </p>
        </div>
      </div>

      <div className="module-card">
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="module-form-grid">
            <div>
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
              />
            </div>
            <div>
              <label>Price</label>
              <input
                type="number"
                value={form.price}
                onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
              />
            </div>
            <div>
              <label>Default Vacancy</label>
              <input
                type="number"
                value={form.vacancy}
                onChange={(e) => setForm((p) => ({ ...p, vacancy: e.target.value }))}
              />
            </div>
            <div>
              <label>Priority</label>
              <input
                type="number"
                value={form.priority}
                onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
              />
            </div>
            <div>
              <label>Start Day Offset</label>
              <input
                type="number"
                value={form.startDayOffset}
                onChange={(e) => setForm((p) => ({ ...p, startDayOffset: e.target.value }))}
              />
            </div>
            <div>
              <label>End Day Offset</label>
              <input
                type="number"
                value={form.endDayOffset}
                onChange={(e) => setForm((p) => ({ ...p, endDayOffset: e.target.value }))}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Description</label>
              <textarea
                rows="3"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div>
              <label>Image</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {form.imageUrl ? (
                  <img
                    src={form.imageUrl}
                    alt="Package"
                    style={{
                      width: '140px',
                      height: '90px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #e2e8f0'
                    }}
                  />
                ) : null}
                <input type="file" accept="image/*" onChange={handleUploadImage} />
                {isUploadingImage ? (
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Uploading...</span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="module-card" style={{ marginTop: '1rem' }}>
            <h3 className="trek-editor-section-title">Includes (this package)</h3>
            <p className="trek-editor-hint">
              Each row: icon (emoji or Lucide slug from the list), title, and optional value shown as
              &quot;Title (value)&quot; on the event page.
              Use {'{stay}'} in value only when this trek has stay options — it is replaced with the selected stay.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {(Array.isArray(form.includesItems) ? form.includesItems : []).map((item, idx) => (
                <div
                  key={`inc-item-${idx}`}
                  style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) 1fr 1fr auto auto auto', gap: '0.5rem', alignItems: 'center' }}
                >
                  <FullIconSelect
                    value={item.icon || ''}
                    onChange={(icon) => setIncludeItemRow(idx, { icon })}
                    defaultLabel="Default (briefcase)"
                  />
                  <input
                    placeholder="Title (e.g. Travelling)"
                    value={item.title || ''}
                    onChange={(e) => setIncludeItemRow(idx, { title: e.target.value })}
                  />
                  <input
                    placeholder="Value (e.g. 3AC Sleeper Train or {stay})"
                    value={item.value || ''}
                    onChange={(e) => setIncludeItemRow(idx, { value: e.target.value })}
                  />
                  <button type="button" className="module-btn secondary" onClick={() => moveIncludeItemRow(idx, -1)} disabled={idx === 0}>
                    ↑
                  </button>
                  <button
                    type="button"
                    className="module-btn secondary"
                    onClick={() => moveIncludeItemRow(idx, 1)}
                    disabled={idx === (Array.isArray(form.includesItems) ? form.includesItems.length : 0) - 1}
                  >
                    ↓
                  </button>
                  <button type="button" className="module-btn secondary" onClick={() => removeIncludeItemRow(idx)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <button type="button" className="module-btn secondary" onClick={addIncludeItemRow}>
                + Add include row
              </button>
            </div>
          </div>

          <div style={{ marginTop: '1rem' }} className="module-section-grid">
            <div className="module-card" style={{ marginBottom: 0 }}>
              <h3 className="trek-editor-section-title">Enabled Schedule</h3>
              <p className="trek-editor-hint" style={{ marginTop: '-0.2rem', marginBottom: '0.45rem' }}>
                Drag and drop rows to change day order.
              </p>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.35rem', maxHeight: 220, overflowY: 'auto' }}>
                {enabledDays.length ? (
                  enabledDays.map((d) => (
                    <div
                      key={d.id}
                      className={`trek-schedule-row ${dragOverScheduleId && String(dragOverScheduleId) === String(d.id) ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', String(d.id));
                        setDraggingScheduleId(d.id);
                        setDragOverScheduleId(d.id);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (String(dragOverScheduleId) !== String(d.id)) setDragOverScheduleId(d.id);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        reorderEnabledSchedules(draggingScheduleId, d.id);
                        setDraggingScheduleId(null);
                        setDragOverScheduleId(null);
                      }}
                      onDragEnd={() => {
                        setDraggingScheduleId(null);
                        setDragOverScheduleId(null);
                      }}
                    >
                      <div className="trek-schedule-row-left">
                        <span className="trek-schedule-grip" aria-hidden="true">::</span>
                        <span>{d.name}</span>
                      </div>
                      <button
                        type="button"
                        className="module-link-btn"
                        onClick={() => toggleSchedule(d.id, false)}
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>No days enabled.</p>
                )}
              </div>
            </div>
            <div className="module-card" style={{ marginBottom: 0 }}>
              <h3 className="trek-editor-section-title">Available Schedule</h3>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.35rem', maxHeight: 220, overflowY: 'auto' }}>
                {availableDays.length ? (
                  availableDays.map((d) => (
                    <div
                      key={d.id}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.25rem 0.35rem' }}
                    >
                      <span>{d.name}</span>
                      <button
                        type="button"
                        className="module-link-btn"
                        onClick={() => toggleSchedule(d.id, true)}
                      >
                        +
                      </button>
                    </div>
                  ))
                ) : (
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>No remaining days.</p>
                )}
              </div>
            </div>
          </div>

          <div className="module-card" style={{ marginTop: '1rem' }}>
            <h3 className="trek-editor-section-title">Reporting Message</h3>
            <textarea
              rows="5"
              style={{ width: '100%', borderRadius: 8, border: '1px solid #cbd5e1', padding: '0.6rem' }}
              value={form.reportingMessage}
              onChange={(e) => setForm((p) => ({ ...p, reportingMessage: e.target.value }))}
            />
            <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.25rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={form.ticketRequired}
                  onChange={(e) => setForm((p) => ({ ...p, ticketRequired: e.target.checked }))}
                />
                Ticket Required
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={form.ticketDeparture}
                  onChange={(e) => setForm((p) => ({ ...p, ticketDeparture: e.target.checked }))}
                />
                Departure
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={form.ticketDepartureAddon}
                  onChange={(e) => setForm((p) => ({ ...p, ticketDepartureAddon: e.target.checked }))}
                />
                Departure + Add-on
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={form.ticketReturn}
                  onChange={(e) => setForm((p) => ({ ...p, ticketReturn: e.target.checked }))}
                />
                Return
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={form.ticketReturnAddon}
                  onChange={(e) => setForm((p) => ({ ...p, ticketReturnAddon: e.target.checked }))}
                />
                Return + Add-on
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={form.hidden}
                  onChange={(e) => setForm((p) => ({ ...p, hidden: e.target.checked }))}
                />
                Hidden
              </label>
            </div>
          </div>

          <div className="legacy-main-actions">
            <button className="module-btn" type="button" disabled={mutation.isLoading} onClick={handleSubmit}>
              {isEdit ? 'Update Package' : 'Save Package'}
            </button>
            <button
              className="module-btn secondary"
              type="button"
              onClick={() => navigate('/treks', { state: { openTrekId: trekId } })}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrekPackageForm;

